(() => {
  if (window.arbEditorialHeaderLoaded) return;
  window.arbEditorialHeaderLoaded = true;
  const instances = new WeakMap();

  const initHeader = (header) => {
    if (!header || header.dataset.initialized === 'true') return;
    header.dataset.initialized = 'true';
    const controller = new AbortController();
    const timers = new Set();
    const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: controller.signal });
    const schedule = (callback, delay) => {
      const timer = window.setTimeout(() => { timers.delete(timer); callback(); }, delay);
      timers.add(timer);
      return timer;
    };

    const drawer = header.querySelector('[data-mobile-drawer]');
    const openButton = header.querySelector('[data-mobile-menu-open]');
    const closeButton = header.querySelector('[data-mobile-menu-close]');
    const backButton = header.querySelector('[data-mobile-panel-back]');
    const drawerTitle = header.querySelector('[data-mobile-drawer-title]');
    const desktopNav = header.querySelector('[data-desktop-nav]');
    const menuList = header.querySelector('[data-header-menu-list]');
    const mobileMount = header.querySelector('[data-mobile-menu-mount]');
    const menuPackages = [...header.querySelectorAll('[data-header-menu-package]')];
    const desktopMenus = [...header.querySelectorAll('[data-desktop-menu]')];
    const desktopMedia = window.matchMedia('(min-width: 1200px)');
    let lastFocused = null;
    let activePackage = null;

    const hydrateMenuChildren = () => {
      menuPackages.forEach((menuPackage) => {
        const source = menuPackage.querySelector('[data-header-child-source]');
        if (!source) return;

        const productTarget = menuPackage.querySelector('[data-header-product-list]');
        const featureTargets = {
          primary: menuPackage.querySelector('[data-header-feature-list="primary"]'),
          secondary: menuPackage.querySelector('[data-header-feature-list="secondary"]')
        };
        let productCount = 0;
        const featureCounts = { primary: 0, secondary: 0 };

        [...source.children].forEach((child) => {
          const childType = child.dataset.arbHeaderChild;
          if (childType === 'product' && productCount < 5 && productTarget) {
            productTarget.append(child);
            productCount += 1;
            return;
          }

          if (childType === 'feature') {
            const group = child.dataset.featureGroup === 'secondary' ? 'secondary' : 'primary';
            if (featureCounts[group] < 2 && featureTargets[group]) {
              featureTargets[group].append(child);
              featureCounts[group] += 1;
            }
          }
        });

        source.remove();
      });
    };

    const mountMenu = () => {
      if (!menuList) return;
      if (desktopMedia.matches) {
        if (menuList.parentElement !== desktopNav) desktopNav?.append(menuList);
      } else if (menuList.parentElement !== mobileMount) {
        mobileMount?.append(menuList);
      }
    };

    const resetPanel = (restoreFocus = false) => {
      const opener = activePackage?.querySelector('[data-open-mobile-package]');
      menuPackages.forEach((menuPackage) => menuPackage.classList.remove('is-mobile-active'));
      activePackage = null;
      if (backButton) backButton.hidden = true;
      if (drawerTitle) drawerTitle.textContent = '';
      if (restoreFocus) opener?.focus();
    };

    const openDrawer = () => {
      if (!drawer) return;
      mountMenu();
      updateMenuViewport();
      lastFocused = document.activeElement;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      openButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      resetPanel();
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        closeButton?.focus();
      });
    };

    const closeDrawer = () => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      schedule(() => {
        drawer.hidden = true;
        resetPanel();
        lastFocused?.focus?.();
      }, 320);
    };

    const bar = header.querySelector('.arb-header__bar');
    let viewportFrame = 0;
    let lastMenuHeight = -1;
    let lastViewportWidth = -1;
    const updateMenuViewport = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const width = document.documentElement.clientWidth;
      const height = Math.min(660, Math.max(0, viewportHeight - (bar?.getBoundingClientRect().bottom || 0) - 12));
      if (height !== lastMenuHeight) {
        header.style.setProperty('--arb-header-menu-height', height + 'px');
        lastMenuHeight = height;
      }
      if (width !== lastViewportWidth) {
        header.style.setProperty('--arb-header-viewport-width', width + 'px');
        lastViewportWidth = width;
      }
    };
    const requestViewportUpdate = () => {
      if (viewportFrame) return;
      viewportFrame = requestAnimationFrame(() => { viewportFrame = 0; updateMenuViewport(); });
    };
    listen(window, 'resize', requestViewportUpdate, { passive: true });
    const updateHeaderSurface = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    listen(window, 'scroll', () => {
      updateHeaderSurface();
      if (drawer?.classList.contains('is-open') || desktopMenus.some(menu => menu.open)) requestViewportUpdate();
    }, { passive: true });
    listen(window, 'pageshow', updateHeaderSurface);
    listen(window.visualViewport, 'resize', requestViewportUpdate, { passive: true });
    updateMenuViewport();
    updateHeaderSurface();

    const closeDesktopMenus = (except = null) => {
      desktopMenus.forEach((menu) => {
        if (menu !== except) menu.removeAttribute('open');
      });
    };

    openButton?.addEventListener('click', openDrawer);
    closeButton?.addEventListener('click', closeDrawer);

    menuPackages.forEach((menuPackage) => {
      const mobileOpener = menuPackage.querySelector('[data-open-mobile-package]');
      mobileOpener?.addEventListener('click', () => {
        activePackage?.classList.remove('is-mobile-active');
        activePackage = menuPackage;
        activePackage.classList.add('is-mobile-active');
        if (drawerTitle) drawerTitle.textContent = menuPackage.dataset.panelTitle || '';
        if (backButton) backButton.hidden = false;
        backButton?.focus();
      });
    });

    backButton?.addEventListener('click', () => resetPanel(true));

    drawer?.addEventListener('click', (event) => {
      if (event.target.closest('a')) closeDrawer();
      if (!event.target.closest('.arb-header__drawer-shell')) closeDrawer();
    });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), summary, input, select')].filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (activePackage) {
        resetPanel(true);
      } else if (drawer?.classList.contains('is-open')) {
        closeDrawer();
      }
      closeDesktopMenus();
    });

    desktopMenus.forEach((menu) => {
      const menuPackage = menu.closest('[data-header-menu-package]');
      let closeTimer;

      menu.querySelector('summary')?.addEventListener('click', (event) => {
        if (!desktopMedia.matches) return;
        event.preventDefault();
        updateMenuViewport();
        const willOpen = !menu.hasAttribute('open');
        closeDesktopMenus(menu);
        menu.toggleAttribute('open', willOpen);
      });

      menuPackage?.addEventListener('mouseenter', () => {
        if (!desktopMedia.matches) return;
        updateMenuViewport();
        window.clearTimeout(closeTimer);
        closeDesktopMenus(menu);
        menu.setAttribute('open', '');
      });

      menuPackage?.addEventListener('mouseleave', () => {
        if (!desktopMedia.matches) return;
        closeTimer = schedule(() => menu.removeAttribute('open'), 120);
      });
    });

    listen(document, 'click', (event) => {
      if (!desktopMedia.matches || header.contains(event.target)) return;
      closeDesktopMenus();
    });

    const handleViewportChange = () => {
      closeDesktopMenus();
      resetPanel();
      if (desktopMedia.matches && drawer?.classList.contains('is-open')) closeDrawer();
      mountMenu();
    };

    instances.set(header, () => {
      controller.abort();
      timers.forEach(timer => window.clearTimeout(timer));
      cancelAnimationFrame(viewportFrame);
      if (drawer?.classList.contains('is-open')) document.documentElement.style.overflow = '';
      delete header.dataset.initialized;
    });
    hydrateMenuChildren();
    listen(desktopMedia, 'change', handleViewportChange);
    mountMenu();
  };
  const visit = (scope, callback) => {
    if (scope.matches?.('[data-arboleaf-editorial-header]')) callback(scope);
    scope.querySelectorAll('[data-arboleaf-editorial-header]').forEach(callback);
  };
  const boot = () => visit(document, initHeader);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => visit(event.target, initHeader));
  document.addEventListener('shopify:section:unload', event => visit(event.target, header => { instances.get(header)?.(); instances.delete(header); }));
})();
